import { useState, useCallback } from 'react';
import { ScoringType, SubjectComponentRow, SubjectSkillRow } from '../types';

export interface MarkBookRowState {
  id?: string;
  firstFieldValue: string;
  scoringType: string;
  maxMarks: string;
  order: string;
}

export const MARK_BOOK_SECTION = {
  COMPONENT: 'component',
  SKILL: 'skill'
} as const;

type MarkBookSection =
  (typeof MARK_BOOK_SECTION)[keyof typeof MARK_BOOK_SECTION];

const emptyRow = (): MarkBookRowState => ({
  firstFieldValue: '',
  scoringType: '',
  maxMarks: '',
  order: ''
});

const needsMaxMarks = (
  row: MarkBookRowState,
  scoringTypeOptions: ScoringType[]
): boolean => {
  const st = scoringTypeOptions.find((s) => s.id === row.scoringType);
  return !!row.scoringType && (!st?.validScores || st.validScores.length === 0);
};

export const mapComponentToRow = (
  c: SubjectComponentRow
): MarkBookRowState => ({
  id: c.id,
  firstFieldValue: c.name,
  scoringType: c.scoringType.id || '',
  maxMarks: c.maxMarks?.toString() ?? '',
  order: c.order.toString()
});

export const mapSkillToRow = (s: SubjectSkillRow): MarkBookRowState => ({
  id: s.id,
  firstFieldValue: s.description,
  scoringType: s.scoringType.id || '',
  maxMarks: s.maxMarks?.toString() ?? '',
  order: s.order.toString()
});

const toComponentRow = (
  row: MarkBookRowState,
  scoringTypeOptions: ScoringType[]
): SubjectComponentRow => {
  const st = scoringTypeOptions.find((s) => s.id === row.scoringType)!;
  return {
    ...(row.id ? { id: row.id } : {}),
    name: row.firstFieldValue,
    scoringType: st,
    order: parseInt(row.order, 10),
    ...(needsMaxMarks(row, scoringTypeOptions) && row.maxMarks
      ? { maxMarks: parseInt(row.maxMarks, 10) }
      : {})
  };
};

const toSkillRow = (
  row: MarkBookRowState,
  scoringTypeOptions: ScoringType[]
): SubjectSkillRow => {
  const st = scoringTypeOptions.find((s) => s.id === row.scoringType)!;
  return {
    ...(row.id ? { id: row.id } : {}),
    description: row.firstFieldValue,
    scoringType: st,
    order: parseInt(row.order, 10),
    ...(needsMaxMarks(row, scoringTypeOptions) && row.maxMarks
      ? { maxMarks: parseInt(row.maxMarks, 10) }
      : {})
  };
};

interface UseMarkBookRowsReturn {
  componentRows: MarkBookRowState[];
  skillRows: MarkBookRowState[];
  rowToDelete: { section: MarkBookSection; index: number } | null;
  addRow: (section: MarkBookSection) => void;
  updateRow: (
    section: MarkBookSection,
    update: { index: number; field: keyof MarkBookRowState; value: string }
  ) => void;
  requestDeleteRow: (section: MarkBookSection, index: number) => void;
  confirmDeleteRow: () => void;
  cancelDeleteRow: () => void;
  resetRows: (
    components?: SubjectComponentRow[],
    skills?: SubjectSkillRow[]
  ) => void;
  mapRowToComponent: (row: MarkBookRowState) => SubjectComponentRow;
  mapRowToSkill: (row: MarkBookRowState) => SubjectSkillRow;
  areAllRowsValid: boolean;
}

export const useMarkBookRows = (
  scoringTypeOptions: ScoringType[]
): UseMarkBookRowsReturn => {
  const [componentRows, setComponentRows] = useState<MarkBookRowState[]>([]);
  const [skillRows, setSkillRows] = useState<MarkBookRowState[]>([]);
  const [rowToDelete, setRowToDelete] = useState<{
    section: MarkBookSection;
    index: number;
  } | null>(null);

  const addRow = (section: MarkBookSection) => {
    const setter = section === 'component' ? setComponentRows : setSkillRows;
    setter((prev) => [...prev, emptyRow()]);
  };

  const updateRow = (
    section: MarkBookSection,
    {
      index,
      field,
      value
    }: { index: number; field: keyof MarkBookRowState; value: string }
  ) => {
    const setter = section === 'component' ? setComponentRows : setSkillRows;
    setter((prev) =>
      prev.map((r, i) => (i === index ? { ...r, [field]: value } : r))
    );
  };

  const requestDeleteRow = (section: MarkBookSection, index: number) => {
    setRowToDelete({ section, index });
  };

  const confirmDeleteRow = () => {
    if (!rowToDelete) return;
    const { section, index } = rowToDelete;
    const setter = section === 'component' ? setComponentRows : setSkillRows;
    setter((prev) => prev.filter((_, i) => i !== index));
    setRowToDelete(null);
  };

  const cancelDeleteRow = () => setRowToDelete(null);

  const resetRows = useCallback(
    (components?: SubjectComponentRow[], skills?: SubjectSkillRow[]) => {
      setComponentRows((components || []).map(mapComponentToRow));
      setSkillRows((skills || []).map(mapSkillToRow));
      setRowToDelete(null);
    },
    []
  );

  const mapRowToComponent = (row: MarkBookRowState) =>
    toComponentRow(row, scoringTypeOptions);

  const mapRowToSkill = (row: MarkBookRowState) =>
    toSkillRow(row, scoringTypeOptions);

  const areAllRowsValid = [...componentRows, ...skillRows].every(
    (row) =>
      row.firstFieldValue &&
      row.scoringType &&
      row.order &&
      (!needsMaxMarks(row, scoringTypeOptions) || row.maxMarks)
  );

  return {
    componentRows,
    skillRows,
    rowToDelete,
    addRow,
    updateRow,
    requestDeleteRow,
    confirmDeleteRow,
    cancelDeleteRow,
    resetRows,
    mapRowToComponent,
    mapRowToSkill,
    areAllRowsValid
  };
};
